import { apiRequest } from "../utils/useAPIRequest";
import constant from "../utils/constant";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../App";
import { fashCreativeStore } from "../stores";

const useGetAddedCatalogues = (
  catalogueTableQuery,
  filterQuery,
  searchQuery
) => {
  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${catalogueTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${catalogueTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${catalogueTableQuery}&${filterQuery}`;
    } else {
      return catalogueTableQuery;
    }
  }, [filterQuery, catalogueTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getCatalogueItems", query],
    queryFn: () => {
      return apiRequest({
        url: `/fashion/catalogue-styles?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const addCatalogue = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/fashion/admin/catalogue-style",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getCatalogueItems"] });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const editCatalogue = async (payload, id) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/fashion/business/admin-catalogue-style?id=${id}`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getCatalogueItems"] });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const getCollections = async (businessId) => {
  let query;
  query = businessId;
  const { status, errorMessage, data } = await apiRequest({
    url: `/fashion/collections?bId=${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    fashCreativeStore.setCollections(data);

    return { status };
  }

  return { status, errorMessage };
};
const getStyleCategories = async () => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/fashion/type-categories",
    method: "get",
  });

  if (status === constant.Success) {
    fashCreativeStore.setCategories(data);

    return { status };
  }

  return { status, errorMessage };
};

const updateStyleCategory = async (payload, id) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/fashion/category/${id}`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    await getStyleCategories();
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const uploadDocument = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/businesses/documents/upload",
    method: "post",
    payload,
    hasImageUpload: true,
  });
  if (status === constant.Success) {
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const useGetThemes = () => {
  return useQuery({
    queryKey: ["getFashThemes"],
    queryFn: () => {
      return apiRequest({
        url: "/fashion/themes",
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const createNewTheme = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/fashion/theme",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const FashCreativesController = {
  addCatalogue,
  useGetAddedCatalogues,
  editCatalogue,
  getCollections,
  getStyleCategories,
  updateStyleCategory,
  uploadDocument,
  createNewTheme,
  useGetThemes,
};

export default FashCreativesController;
